import { useEffect, useRef } from 'react';
import { Banner } from '@airbus/components-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useNavigate } from 'react-router';
import Spinners from '../../shared/components/Spinner';
import Layout from '../../shared/components/layout';
import useFetchScreenDetails from '../../shared/utils/useFetchScreenDetails';
import GetAllColumnContextProvider, {
  useColumnContextAPI,
} from '../../shared/context/GetAllColumnContext';
import {
  PSPActions,
  extraValidator as pspExtraValidator,
  setPspManagementDropdownOptions,
} from '../../features/psp-management';
import GridWrapper from '../../shared/components/grid/GridWrapper';
import { PspConfirmBeforeCommit } from '../../features/psp-management/services/PspConfirmBeforeCommit';
import { SetColumnConfig as pspSetColumnConfig } from '../../features/psp-management/services/pspColumnConfig';
import { setAllDropdowns } from '../../shared/services/allDropdownsRegistry';
import { useLanguageContextAPI } from '../../shared/context/LanguageContext';

const PSPManagementPage = () => {
  const navigate = useNavigate();
  const pspEnabledFlag = useFeatureFlagEnabled('newPspManagement');
  const pspEnabled = pspEnabledFlag === true;
  const isPspFlagResolved = typeof pspEnabledFlag === 'boolean';
  const { loading, error, screenTables } = useFetchScreenDetails('psp_management');
  const { t } = useLanguageContextAPI();

  useEffect(() => {
    if (pspEnabledFlag === false) {
      navigate('/404');
    }
  }, [navigate, pspEnabledFlag]);

  if (!isPspFlagResolved) {
    return <Spinners />;
  }

  if (!pspEnabled) {
    return null;
  }

  return (
    <>
      {loading ? (
        <Spinners />
      ) : (
        <Layout>
          {error && <Banner variant='warning'>{t('data_not_available')}</Banner>}
          {!screenTables
            ? null
            : screenTables.map((item) => (
                <GetAllColumnContextProvider key={item.id}>
                  <PSPManagement tableRes={item} />
                </GetAllColumnContextProvider>
              ))}
        </Layout>
      )}
    </>
  );
};

export default PSPManagementPage;

const PSPManagement = ({ tableRes }) => {
  const { updateCustomConfig, allRows, customConfig, allDropdowns } = useColumnContextAPI();

  const allRowsRef = useRef(allRows);
  allRowsRef.current = allRows;
  const getAllRows = () => (Array.isArray(allRowsRef.current) ? allRowsRef.current : []);

  useEffect(() => {
    setAllDropdowns(allDropdowns);
  }, [allDropdowns]);

  const initNewRowActive = ({ row }: { row: any }) => {
    const yesItem = allDropdowns?.active?.find(
      (item: { label: string; value: any }) => item.label?.toLowerCase() === 'yes',
    );
    const ACTIVE_YES = yesItem?.value ?? 1;

    if (row?.status === 'N' && row.active == null) {
      row.active = ACTIVE_YES;
    }
    return row;
  };

  useEffect(() => {
    updateCustomConfig({
      Actions: PSPActions,
      extraValidator: pspExtraValidator,
      ConfirmBeforeCommit: PspConfirmBeforeCommit,
      NewRowInitializer: initNewRowActive,
      SetDropdownOptions: setPspManagementDropdownOptions,
      SetIsCellEditable: ({ field, row, access }) =>
        field === 'active' && row?.status === 'N' ? false : row?.status !== 'D' && access?.edit_row,
      SetColumnConfig: (cfgProps) =>
        pspSetColumnConfig({
          ...cfgProps,
          tableRes,
          customProps: {
            ...(cfgProps.customProps ?? {}),
            updateCustomConfig,
            customConfig,
            getAllRows,
          },
        }),
    });
  }, []);

  return <GridWrapper tableRes={tableRes} />;
};
